// Client-side KB matching, shared by the home hero search and the new-request
// deflection panel. The whole published KB is small and fetched once (GET /kb),
// so a simple token scorer beats a network round-trip per keystroke.

import type { KbArticle } from "./types";

/** Lowercased word tokens, dropping 1-character noise ("a", "i", punctuation). */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
}

/**
 * Rank articles against a free-text query. Scoring favors title hits over body
 * hits, and whole-phrase title matches over scattered tokens. Returns the top
 * `limit` matches (score > 0), best first.
 */
export function matchArticles(articles: KbArticle[], query: string, limit = 5): KbArticle[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const tokens = tokenize(q);
  if (tokens.length === 0) return [];

  const scored: { article: KbArticle; score: number }[] = [];
  for (const a of articles) {
    const title = a.title.toLowerCase();
    const body = a.body_md.toLowerCase();
    let score = 0;
    if (title.includes(q)) score += 10; // whole phrase in the title
    for (const t of tokens) {
      if (title.includes(t)) score += 3;
      else if (body.includes(t)) score += 1;
    }
    if (score > 0) scored.push({ article: a, score });
  }
  scored.sort((x, y) => y.score - x.score);
  return scored.slice(0, limit).map((s) => s.article);
}
